/**
 * Редактор «Авансова реєстрація ПДВ» (рішення власника 2026-07-28:
 * «нужна нормальная страница создания» — раніше був лише список).
 *
 * Аванс = перша подія за ПКУ ст.187: отримана передоплата → податкове
 * зобов'язання. Документ створюється НА ПІДСТАВІ надходження грошей
 * (банк/каса): кнопка «обрати надходження» → попап проведених надходжень
 * покупця → сума/контрагент/дата підтягуються. Проведення: авансова ПН
 * (реквізити покупця + рядок «попередня оплата») + Дт 6431 — Кт 6432 (ч.1445, схема 1С БП).
 * Далі ПН реєструється в ЄРПН зі своєї сторінки.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { advancePayments, type AdvancePaymentCreate, type AdvancePaymentItem } from '@/api/vatDocuments';
import { taxInvoicesOutgoing } from '@/api/taxInvoices';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import { bankReceipts } from '@/api/bankDocs';
import { customerOrders } from '@/api/orders';
import { cashReceipts } from '@/api/cashDocs';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import SearchSelect from '@/components/SearchSelect';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';

function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { useDefaults } from '@/store/defaults';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

let _advRowSeq = 0;
const newRowKey = () => `ar${++_advRowSeq}`;

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface SourceRow {
  id: string;
  kind: 'bank_receipt' | 'cash_receipt';
  document_number: string;
  document_date: string;
  amount: string;
  contractor_id: string | null;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  source_doc_type: 'bank_receipt' | 'cash_receipt' | '';
  source_doc_id: string;
  advance_amount: string;
  vat_rate: string;
  base_order_id: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  source_doc_type: '',
  source_doc_id: '',
  advance_amount: '',
  vat_rate: '20',
  base_order_id: '',
  comment: '',
};

export default function AdvancePaymentEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const [f, setF] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [srcPopup, setSrcPopup] = useState(false);
  const [orderPopup, setOrderPopup] = useState(false);
  // ТЧ (0400): рядки майбутньої авансової ПН — як у вихідній ПН.
  interface ItemRow {
    _key: string;
    nomenclature_id: string | null;
    description: string;
    ukt_zed_code: string | null;
    quantity: string;
    price_without_vat: string;
    vat_rate_id: string;
  }
  const [rows, setRows] = useState<ItemRow[]>([]);
  const [erpnBusy, setErpnBusy] = useState(false);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('documents.advance_payment_tax_reg.create');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const vatRatesQ = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', f.organization_id, f.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: f.organization_id, contractor_id: f.contractor_id,
    }),
    enabled: !!f.organization_id && !!f.contractor_id,
  });

  const [searchParams] = useSearchParams();
  const srcApplied = useRef(false);
  useEffect(() => {
    if (!isNew || srcApplied.current) return;
    const st = searchParams.get('source_type');
    const sid = searchParams.get('source_id');
    if ((st !== 'bank_receipt' && st !== 'cash_receipt') || !sid) return;
    srcApplied.current = true;
    // ч.1446: сума — авансова частина надходження (решта гасить борг), як рахує сервер.
    // ч.1447: і з каси — реєстрація лишилась для надходжень, проведених без ПДВ з авансу.
    const load = st === 'bank_receipt' ? bankReceipts.get(sid) : cashReceipts.get(sid);
    load.then((r) => taxInvoicesOutgoing.advanceFromReceiptState(
      r.organization_id, st === 'bank_receipt' ? 'doc.bank_receipts' : 'doc.cash_receipts', r.id,
    ).catch(() => null).then((sug) => [r, sug] as const)).then(([r, sug]) => {
      setF((s) => ({
        ...s,
        organization_id: r.organization_id,
        source_doc_type: st,
        source_doc_id: r.id,
        advance_amount: String(sug?.advance_amount || r.amount),
        contractor_id: r.contractor_id ?? s.contractor_id,
        document_date: (r.document_date ?? s.document_date).slice(0, 16),
      }));
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, searchParams]);

  const docQ = useQuery({
    queryKey: ['advance-payment', id],
    queryFn: () => advancePayments.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(f, setF, { ready: isNew || !!docQ.data });
  const d = docQ.data;

  // Завантаження існуючого документа у форму.
  useEffect(() => {
    if (!d) return;
    setF({
      organization_id: d.organization_id,
      document_number: d.document_number,
      document_date: d.document_date.slice(0, 16),
      contractor_id: d.contractor_id,
      contract_id: d.contract_id ?? '',
      source_doc_type: d.source_doc_type,
      source_doc_id: d.source_doc_id,
      advance_amount: String(d.advance_amount),
      vat_rate: String(Number(d.vat_rate)),
      base_order_id: d.base_order_id ?? '',
      comment: d.comment ?? '',
    });
    setRows((d.items ?? []).map((it, i) => ({
      _key: `ld${i}`,
      nomenclature_id: it.nomenclature_id ?? null,
      description: it.description,
      ukt_zed_code: it.ukt_zed_code ?? null,
      quantity: String(it.quantity),
      price_without_vat: String(it.price_without_vat),
      vat_rate_id: it.vat_rate_id,
    })));
  }, [d]);

  // Дефолтна організація для нового документа.
  useEffect(() => {
    if (!isNew || f.organization_id || !availableOrgs.length) return;
    const preferred = availableOrgs.find((o) => o.id === defaultOrgId) ?? availableOrgs[0];
    if (preferred) setF((s) => ({ ...s, organization_id: preferred.id }));
  }, [isNew, availableOrgs, defaultOrgId, f.organization_id]);

  // Підстава (№/дата платіжки або касового ордера) для підпису біля кнопки.
  const srcDocQ = useQuery({
    queryKey: ['adv-src-doc', f.source_doc_type, f.source_doc_id],
    queryFn: async (): Promise<{ document_number: string; document_date: string }> =>
      f.source_doc_type === 'bank_receipt'
        ? bankReceipts.get(f.source_doc_id)
        : cashReceipts.get(f.source_doc_id),
    enabled: !!f.source_doc_id && !!f.source_doc_type,
  });
  const srcDoc = srcDocQ.data;

  // Згенерована авансова ПН — статус ЄРПН + подача у Кабінет ДПС.
  const genPnQ = useQuery({
    queryKey: ['adv-gen-pn', d?.generated_tax_invoice_id],
    queryFn: () => taxInvoicesOutgoing.get(d!.generated_tax_invoice_id!),
    enabled: !!d?.generated_tax_invoice_id,
    refetchInterval: 30_000,
  });
  async function registerGeneratedPn() {
    if (!d?.generated_tax_invoice_id) return;
    setErpnBusy(true);
    try {
      await taxInvoicesOutgoing.erpnDraft(d.generated_tax_invoice_id);
      qc.invalidateQueries({ queryKey: ['adv-gen-pn'] });
      qc.invalidateQueries({ queryKey: ['dps-submissions'] });
      navigate('/dps-cabinet');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    } finally {
      setErpnBusy(false);
    }
  }

  const numberPreview = useNumberPreview(
    'doc.advance_payment_tax_registration', f.organization_id || undefined, isNew);

  // Попап «обрати надходження»: проведені банк + каса поточної org
  // (фільтр по покупцю, якщо вже обраний).
  const bankQ = useQuery({
    queryKey: ['adv-bank-receipts', f.organization_id, f.contractor_id],
    queryFn: () => bankReceipts.list({
      organization_id: f.organization_id,
      contractor_id: f.contractor_id || undefined,
      posted: true, limit: 200,
    }),
    enabled: srcPopup && !!f.organization_id,
  });
  const cashQ = useQuery({
    queryKey: ['adv-cash-receipts', f.organization_id, f.contractor_id],
    queryFn: () => cashReceipts.list({
      organization_id: f.organization_id,
      contractor_id: f.contractor_id || undefined,
      posted: true, limit: 200,
    }),
    enabled: srcPopup && !!f.organization_id,
  });
  // Рахунки/замовлення покупця — джерело ТОВАРНИХ РЯДКІВ авансової ПН.
  const ordersQ = useQuery({
    queryKey: ['adv-orders', f.organization_id, f.contractor_id],
    queryFn: () => customerOrders.list({
      organization_id: f.organization_id,
      contractor_id: f.contractor_id || undefined,
      limit: 200,
    }),
    enabled: (orderPopup || !!f.base_order_id) && !!f.organization_id,
  });
  const baseOrder = (ordersQ.data?.items ?? []).find((o) => o.id === f.base_order_id);

  const sourceRows: SourceRow[] = useMemo(() => {
    const bank = (bankQ.data?.items ?? []).map((r) => ({
      id: r.id, kind: 'bank_receipt' as const,
      document_number: r.document_number, document_date: r.document_date,
      amount: r.amount, contractor_id: r.contractor_id,
    }));
    const cash = (cashQ.data?.items ?? []).map((r: any) => ({
      id: r.id, kind: 'cash_receipt' as const,
      document_number: r.document_number, document_date: r.document_date,
      amount: r.amount, contractor_id: r.contractor_id ?? null,
    }));
    return [...bank, ...cash].sort(
      (a, b) => (b.document_date ?? '').localeCompare(a.document_date ?? ''));
  }, [bankQ.data, cashQ.data]);

  const contractorName = useMemo(() => {
    const m = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [contractorsQ.data]);

  const amount = Number(f.advance_amount) || 0;
  const rate = Number(f.vat_rate) || 0;
  const vatAmount = amount * rate / (100 + rate);

  const rates = vatRatesQ.data ?? [];
  const rateById = useMemo(() => {
    const m = new Map<string, { code: string; pct: number }>();
    rates.forEach((r) => m.set(r.id, { code: r.code, pct: Number(r.rate_percent) }));
    return m;
  }, [rates]);
  const rate20Id = rates.find((r) => r.code === 'VAT_20')?.id ?? '';
  const nomById = useMemo(() => {
    const m = new Map<string, { name: string; ukt?: string | null }>();
    nomListQ.data?.items.forEach((n) => m.set(n.id, { name: n.name, ukt: n.ukt_zed_code }));
    return m;
  }, [nomListQ.data]);

  function lineAmounts(r: ItemRow): { base: number; vat: number } {
    const base = Math.round((Number(r.quantity) || 0) * (Number(r.price_without_vat) || 0) * 100) / 100;
    const vr = rateById.get(r.vat_rate_id);
    const vat = vr && ['VAT_20', 'VAT_7', 'VAT_14'].includes(vr.code)
      ? Math.round(base * vr.pct) / 100 : 0;
    return { base, vat };
  }
  const rowTotals = useMemo(() => {
    let base = 0, vat = 0;
    for (const r of rows) { const a = lineAmounts(r); base += a.base; vat += a.vat; }
    return { base, vat, total: base + vat };
  }, [rows, rateById]);
  const totalsMismatch = rows.length > 0
    && Math.abs(rowTotals.total - amount) > 0.02;

  const setRow = (key: string, patch: Partial<ItemRow>) =>
    setRows((s0) => s0.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  // Авто-заповнення ТЧ з рахунку (серверна пропорція — Закон #6).
  async function fillRowsFromOrder(orderId: string, advAmount: string) {
    if (!orderId || !(Number(advAmount) > 0)) return;
    try {
      const { items } = await advancePayments.linesFromOrder({
        organization_id: f.organization_id,
        base_order_id: orderId,
        advance_amount: advAmount,
      });
      setRows(items.map((it, i) => ({
        _key: `fo${Date.now()}-${i}`,
        nomenclature_id: it.nomenclature_id ?? null,
        description: it.description,
        ukt_zed_code: it.ukt_zed_code ?? null,
        quantity: String(it.quantity),
        price_without_vat: String(it.price_without_vat),
        vat_rate_id: it.vat_rate_id,
      })));
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? String(e));
    }
  }

  function pickSource(r: SourceRow) {
    setF((s) => ({
      ...s,
      source_doc_type: r.kind,
      source_doc_id: r.id,
      advance_amount: String(r.amount),
      contractor_id: r.contractor_id ?? s.contractor_id,
      document_date: (r.document_date ?? s.document_date).slice(0, 16),
    }));
    setSrcPopup(false);
  }

  function buildPayload(): AdvancePaymentCreate {
    if (!f.organization_id) throw new Error('Оберіть організацію');
    if (!f.contractor_id) throw new Error('Оберіть покупця');
    if (!f.source_doc_id || !f.source_doc_type) {
      throw new Error('Оберіть надходження грошей (банк/каса) — аванс реєструється на підставі оплати');
    }
    if (!(amount > 0)) throw new Error('Вкажіть суму авансу');
    return {
      organization_id: f.organization_id,
      document_number: f.document_number || undefined,
      document_date: new Date(f.document_date).toISOString(),
      contractor_id: f.contractor_id,
      contract_id: f.contract_id || null,
      source_doc_type: f.source_doc_type,
      source_doc_id: f.source_doc_id,
      advance_amount: f.advance_amount,
      vat_rate: f.vat_rate,
      base_order_id: f.base_order_id || null,
      items: rows
        .filter((r) => r.description.trim() || r.nomenclature_id)
        .map((r): AdvancePaymentItem => {
          const a = lineAmounts(r);
          return {
            nomenclature_id: r.nomenclature_id,
            description: r.description.trim()
              || (r.nomenclature_id ? (nomById.get(r.nomenclature_id)?.name ?? '—') : '—'),
            ukt_zed_code: r.ukt_zed_code,
            quantity: r.quantity,
            price_without_vat: r.price_without_vat,
            vat_rate_id: r.vat_rate_id,
            base_amount: String(a.base),
            vat_amount: String(a.vat),
          };
        }),
      comment: f.comment || null,
    };
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['advance-payments'] });
    qc.invalidateQueries({ queryKey: ['advance-payment', id] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      if (isNew) return advancePayments.create(payload);
      // ч.1339: покупець і надходження теж зберігаються (раніше губились мовчки).
      const { organization_id: _o, document_number: _n, ...upd } = payload;
      return advancePayments.update(id!, upd);
    },
    onSuccess: (res) => {
      setError(null); invalidate();
      if (isNew) navigate(`/taxation/advance-payment-registrations/${res.id}`, { replace: true });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  const saveAndPost = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      const doc = isNew ? await advancePayments.create(payload)
        : (await advancePayments.update(id!, {
            document_date: payload.document_date, comment: payload.comment,
            contractor_id: payload.contractor_id,
            source_doc_type: payload.source_doc_type, source_doc_id: payload.source_doc_id,
            contract_id: payload.contract_id,
            advance_amount: payload.advance_amount, vat_rate: payload.vat_rate,
            base_order_id: payload.base_order_id, items: payload.items,
          }), await advancePayments.get(id!));
      try {
        return await advancePayments.post(doc.id);
      } catch (e) {
        // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
        if (isNew) navigate(`/taxation/advance-payment-registrations/${doc.id}`, { replace: true });
        throw e;
      }
    },
    onSuccess: (res) => {
      setError(null); invalidate();
      navigate(`/taxation/advance-payment-registrations/${res.id}`, { replace: true });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  const unpost = useMutation({
    mutationFn: () => advancePayments.unpost(id!),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  const remove = useMutation({
    mutationFn: () => advancePayments.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advance-payments'] });
      navigate('/taxation/advance-payment-registrations');
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  if (!isNew && !d) return <div className="card">Завантаження…</div>;
  if (isNew && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        У вас немає прав створювати авансові реєстрації у жодній з{' '}
        {totalAccessibleOrgs} ваших організацій.
      </div>
    );
  }

  const isPosted = !!d?.posted;
  const readOnly = isPosted;

  // ч.889 (новий дизайн, Закон #10): «ще дій» не було; ЄРПН/«відкрити ПН» — у банері.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (f.organization_id ? 1 : 0) + (f.document_date ? 1 : 0) },
    { id: 'party', title: 'Покупець і аванс', required: 2,
      filled: (f.contractor_id ? 1 : 0) + (Number(f.advance_amount) > 0 ? 1 : 0) },
    { id: 'items', title: 'Рядки авансової ПН', required: 0, filled: 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова авансова ПН'
            : `Авансова ПН ${d!.document_number}`}
        </h2>
        <div style={{ marginLeft: 'auto' }}>
          <DocumentToolbar
            isNew={isNew}
            entriesDoc={{ type: 'doc.advance_payment_tax_registration', id: isNew ? '' : id! }}
            railMode
            extraActions={railActions}
            onBackToList={() => navigate('/taxation/advance-payment-registrations')}
            onSave={() => save.mutate()}
            saveDisabled={readOnly}
            savePending={save.isPending}
            isPosted={isPosted}
            onPost={() => saveAndPost.mutate()}
            onUnpost={() => unpost.mutate()}
            postPending={saveAndPost.isPending}
            unpostPending={unpost.isPending}
            postTitle="Зберігає та проводить: створюється авансова ПН + проводка Дт 6431 — Кт 6432; на 6412 ПДВ перенесе проведення цієї ПН"
            onPrint={d?.generated_tax_invoice_id
              ? () => navigate(`/tax-invoices-outgoing/${d.generated_tax_invoice_id}/print`)
              : undefined}
            deletable={!isPosted && !isNew}
            onDelete={() => {
              confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); });
            }}
          />
        </div>
      </div>

      {error && (
        <div style={{ color: '#dc2626', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>
      )}

      {/* ЄРПН — та сама механіка, що вихідна ПН (чернетка у Кабінеті ДПС →
          підпис КЕП і відправка там). Статус — з самої згенерованої ПН. */}
      {!isNew && !d?.generated_tax_invoice_id && (
        <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
          color: 'var(--badge-blue-fg)', padding: '0.5rem 0.75rem', borderRadius: 6,
          marginBottom: '0.75rem', display: 'flex', gap: '0.5rem',
          alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            {isPosted
              ? 'авансову ПН ще не створено — розпровести і провести знову'
              : 'проведіть документ — створиться авансова ПН, тоді її можна передати на реєстрацію в ЄРПН'}
          </span>
          <a style={{ cursor: 'pointer', marginLeft: 'auto' }}
            onClick={() => navigate('/dps-cabinet')}>Кабінет ДПС →</a>
        </div>
      )}
      {d?.generated_tax_invoice_id && (
        <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
          color: 'var(--badge-blue-fg)', padding: '0.5rem 0.75rem', borderRadius: 6,
          marginBottom: '0.75rem', display: 'flex', gap: '0.5rem',
          alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            {genPnQ.data?.erpn_status === 'REGISTERED'
              ? `✓ авансову ПН зареєстровано в ЄРПН${genPnQ.data.erpn_registration_date
                ? ` від ${new Date(genPnQ.data.erpn_registration_date).toLocaleDateString('uk-UA')}` : ''}`
              : genPnQ.data?.erpn_status === 'REJECTED' ? '✗ відмовлено у реєстрації ПН'
              : genPnQ.data?.erpn_status === 'STOPPED' ? '⏸ реєстрацію ПН зупинено (СМКОР)'
              : genPnQ.data?.erpn_status === 'SENT' ? '→ ПН відправлена на реєстрацію, обробляється'
              : genPnQ.data?.erpn_status === 'DRAFT'
                ? 'чернетка подачі у Кабінеті ДПС — підпишіть і відправте там'
              : genPnQ.data?.erpn_status === 'ERROR' ? '✗ помилка відправки — див. Кабінет ДПС'
              : 'авансову ПН створено — передайте на реєстрацію в ЄРПН'}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem' }}>
            {genPnQ.data?.erpn_status === 'NOT_SENT' ? (
              <a style={{ cursor: 'pointer' }}
                onClick={() => { if (!erpnBusy) void registerGeneratedPn(); }}>
                {erpnBusy ? 'готується…' : 'передати у Кабінет ДПС →'}
              </a>
            ) : (
              <a style={{ cursor: 'pointer' }} onClick={() => navigate('/dps-cabinet')}>
                Кабінет ДПС →
              </a>
            )}
            <a style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/tax-invoices-outgoing/${d.generated_tax_invoice_id}`)}>
              відкрити ПН →
            </a>
          </span>
        </div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.advance_payment_tax_registration', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover value={f.document_date} withTime
            onChange={(v) => setF((s) => ({ ...s, document_date: v }))} />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Організація *</label>
          <select value={f.organization_id} disabled={!isNew}
            onChange={(e) => setF((s) => ({
              ...s, organization_id: e.target.value,
              contract_id: '', source_doc_id: '', source_doc_type: '',
            }))}>
            <option value="">— оберіть —</option>
            {(isNew ? availableOrgs : (orgList.data?.items ?? [])).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>№</label>
          <input value={f.document_number || numberPreview || ''} readOnly
            placeholder="авто" />
        </div>
      </div>

      </DocBlock>

      <DocBlock index={2} title="Покупець і аванс">
      <div className="doc-grid" style={{ alignItems: 'end' }}>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Покупець *</label>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou,
            }))}
            value={f.contractor_id}
            disabled={readOnly}
            onChange={(cid) => {
              // Інший покупець → його платіжки/рахунки: чистимо все залежне.
              setF((s) => ({
                ...s, contractor_id: cid, contract_id: '',
                source_doc_type: '', source_doc_id: '',
                advance_amount: '', base_order_id: '',
              }));
              setRows([]);
            }}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Договір</label>
          <select value={f.contract_id} disabled={readOnly || !f.contractor_id}
            onChange={(e) => setF((s) => ({ ...s, contract_id: e.target.value }))}>
            <option value="">без договору</option>
            {(contractsQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          {/* Механіка «Підстава» (еталон ч.246); ч.889 — канон ч.861: підпис
              «Підстава» лише при звʼязку, кнопка — поки звʼязку немає. */}
          <label>{f.source_doc_id ? 'Підстава' : '\u00A0'}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {!f.source_doc_id && (
            <button type="button" className="btn-doc" disabled={readOnly || !f.organization_id}
              onClick={() => setSrcPopup(true)}
              title={!f.organization_id ? 'Спершу оберіть організацію' : undefined}>
              <DocIcon />обрати надходження
            </button>
            )}
            {f.source_doc_id && (
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                  onClick={() => navigate(f.source_doc_type === 'bank_receipt'
                    ? `/bank-receipts/${f.source_doc_id}`
                    : `/cash-receipts/${f.source_doc_id}`)}>
                  {f.source_doc_type === 'bank_receipt' ? 'банк' : 'каса'}
                  {srcDoc
                    ? ` №${srcDoc.document_number} від ${new Date(srcDoc.document_date).toLocaleDateString('uk-UA')}`
                    : ''}
                </a>
                {!readOnly && (
                  <button type="button" className="btn-x"
                    onClick={() => setF((s) => ({
                      ...s, source_doc_type: '', source_doc_id: '',
                      advance_amount: '',
                    }))}
                    title="Зняти звʼязок з надходженням (сума авансу очищається)">×</button>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Сума авансу (з ПДВ) *</label>
          <input type="number" min="0" step="0.01" value={f.advance_amount}
            disabled={readOnly}
            onChange={(e) => setF((s) => ({ ...s, advance_amount: e.target.value }))} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Ставка ПДВ</label>
          <select value={f.vat_rate} disabled={readOnly}
            onChange={(e) => setF((s) => ({ ...s, vat_rate: e.target.value }))}>
            <option value="20">20%</option>
            <option value="14">14%</option>
            <option value="7">7%</option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>ПДВ (розрахунково)</label>
          <input readOnly value={fmt(vatAmount)} />
        </div>
      </div>

      </DocBlock>

      {/* ТЧ (0400): рядки майбутньої авансової ПН — механіка вихідної ПН;
          ч.889 — «обрати рахунок» / «+ рядок» у заголовку блоку. */}
      <DocBlock index={3} title="Рядки авансової ПН"
        actions={!readOnly ? (<>
          {f.base_order_id ? (
            <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {baseOrder
                ? `рахунок №${baseOrder.document_number} від ${new Date(baseOrder.document_date).toLocaleDateString('uk-UA')}`
                : 'рахунок обрано'}
              <button type="button" className="btn-x"
                onClick={() => {
                  setF((s0) => ({ ...s0, base_order_id: '' }));
                  setRows([]);
                }}
                title="Зняти звʼязок з рахунком (рядки очищаються)">×</button>
            </span>
          ) : (
            <button type="button" className="btn-doc"
              disabled={!f.organization_id}
              title="Рядки заповняться з рахунку пропорційно сумі авансу (УКТЗЕД і ставки — з карток номенклатури); повторний вибір перезаповнює"
              onClick={() => setOrderPopup(true)}>
              <DocIcon />обрати рахунок
            </button>
          )}
          <button type="button" className="primary"
            onClick={() => setRows((s0) => [...s0, {
              _key: newRowKey(), nomenclature_id: null, description: '',
              ukt_zed_code: null, quantity: '1', price_without_vat: '0',
              vat_rate_id: rate20Id,
            }])}>+ рядок</button>
        </>) : undefined}>
      {!readOnly && f.base_order_id && !baseOrder && null}
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th style={{ minWidth: 180 }}>Номенклатура</th>
            <th style={{ minWidth: 160 }}>Опис</th>
            <th style={{ width: 110 }}>УКТЗЕД</th>
            <th style={{ width: 85, textAlign: 'right' }}>К-сть</th>
            <th style={{ width: 100, textAlign: 'right' }}>Ціна без ПДВ</th>
            <th style={{ width: 95 }}>Ставка</th>
            <th style={{ width: 95, textAlign: 'right' }}>База</th>
            <th style={{ width: 90, textAlign: 'right' }}>ПДВ</th>
            <th style={{ width: 100, textAlign: 'right' }}>Усього</th>
            {!readOnly && <th style={{ width: 30 }}></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const a = lineAmounts(r);
            return (
              <tr key={r._key}>
                <td>{i + 1}</td>
                <td>
                  <NomenclatureSelect value={r.nomenclature_id ?? ''} disabled={readOnly}
                    onChange={(nid) => setRow(r._key, {
                      nomenclature_id: nid || null,
                      description: r.description || (nomById.get(nid)?.name ?? ''),
                      ukt_zed_code: r.ukt_zed_code || (nomById.get(nid)?.ukt ?? null),
                    })} />
                </td>
                <td>
                  <input value={r.description} disabled={readOnly}
                    onChange={(e) => setRow(r._key, { description: e.target.value })} />
                </td>
                <td>
                  <input value={r.ukt_zed_code ?? ''} disabled={readOnly} placeholder="—"
                    onChange={(e) => setRow(r._key, { ukt_zed_code: e.target.value || null })} />
                </td>
                <td>
                  <input type="number" min="0" step="0.0001" value={r.quantity}
                    disabled={readOnly} style={{ textAlign: 'right' }}
                    onChange={(e) => setRow(r._key, { quantity: e.target.value })} />
                </td>
                <td>
                  <input type="number" min="0" step="0.0001" value={r.price_without_vat}
                    disabled={readOnly} style={{ textAlign: 'right' }}
                    onChange={(e) => setRow(r._key, { price_without_vat: e.target.value })} />
                </td>
                <td>
                  <select value={r.vat_rate_id} disabled={readOnly}
                    onChange={(e) => setRow(r._key, { vat_rate_id: e.target.value })}>
                    {rates.map((vr) => (
                      <option key={vr.id} value={vr.id}>{vr.name}</option>
                    ))}
                  </select>
                </td>
                <td style={{ textAlign: 'right' }}>{fmt(a.base)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(a.vat)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {fmt(a.base + a.vat)}
                </td>
                {!readOnly && (
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ cursor: 'pointer', color: '#dc2626' }}
                      title="прибрати рядок"
                      onClick={() => setRows((s0) => s0.filter((x) => x._key !== r._key))}>×</span>
                  </td>
                )}
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={11} style={{ color: 'var(--text-secondary)' }}>
              Немає рядків — оберіть рахунок (заповняться пропорційно авансу)
              або додайте вручну. Без рядків ПН матиме один рядок «попередня
              оплата».
            </td></tr>
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={7} style={{ textAlign: 'right' }}>Разом:</td>
              <td style={{ textAlign: 'right' }}>{fmt(rowTotals.base)}</td>
              <td style={{ textAlign: 'right' }}>{fmt(rowTotals.vat)}</td>
              <td style={{ textAlign: 'right' }}>{fmt(rowTotals.total)}</td>
              {!readOnly && <td></td>}
            </tr>
          </tfoot>
        )}
      </TchTable>
      {totalsMismatch && (
        <div style={{ color: 'var(--badge-amber-fg)', background: 'var(--badge-amber-bg)',
          border: '1px solid var(--badge-amber-bd)', borderRadius: 6,
          padding: '0.4rem 0.75rem', margin: '0.5rem 0', fontSize: '0.85rem' }}>
          ⚠ Сума рядків з ПДВ ({fmt(rowTotals.total)}) не збігається з сумою
          авансу ({fmt(amount)}) — виправте рядки або оберіть рахунок заново (× біля
          рахунку, потім «обрати рахунок») — рядки перезаповняться пропорційно сумі авансу.
        </div>
      )}

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={f.comment} disabled={readOnly}
            onChange={(e) => setF((s) => ({ ...s, comment: e.target.value }))} />
        </div>
        <div>
          {/* ч.889: вкладень у авансової ПН раніше не було. */}
          <AttachmentsPanel organizationId={f.organization_id}
            documentType="doc.advance_payment_tax_registration" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>

      {srcPopup && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--overlay)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60,
        }} onClick={() => setSrcPopup(false)}>
          <div style={{
            background: 'var(--bg-panel)', borderRadius: 10, padding: '1rem',
            width: 760, maxHeight: '75vh', overflow: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Оберіть надходження грошей</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setSrcPopup(false)}>закрити</button>
            </div>
            {(bankQ.isLoading || cashQ.isLoading) && <div>Завантаження…</div>}
            {!bankQ.isLoading && !cashQ.isLoading && sourceRows.length === 0 && (
              <div style={{ color: 'var(--text-secondary)' }}>
                Немає проведених надходжень{f.contractor_id ? ' цього покупця' : ''}.
              </div>
            )}
            {sourceRows.length > 0 && (
              <TchTable>
                <thead>
                  <tr>
                    <th>Джерело</th>
                    <th>№</th>
                    <th>Дата</th>
                    <th>Платник</th>
                    <th style={{ textAlign: 'right' }}>Сума</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sourceRows.map((r) => (
                    <tr key={r.id} style={{ cursor: 'pointer' }}
                      onDoubleClick={() => pickSource(r)}>
                      <td>
                        {r.kind === 'bank_receipt' ? 'банк' : 'каса'}
                      </td>
                      <td>{r.document_number}</td>
                      <td>
                        {r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : ''}
                      </td>
                      <td>
                        {r.contractor_id ? (contractorName.get(r.contractor_id) ?? '') : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmt(Number(r.amount))}</td>
                      <td>
                        <button onClick={() => pickSource(r)}>обрати</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TchTable>
            )}
          </div>
        </div>
      )}

      {/* Попап вибору рахунку/замовлення покупця (товарні рядки ПН). */}
      {orderPopup && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--overlay)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60,
        }} onClick={() => setOrderPopup(false)}>
          <div style={{
            background: 'var(--bg-panel)', borderRadius: 10, padding: '1rem',
            width: 720, maxHeight: '75vh', overflow: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Оберіть рахунок/замовлення покупця</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setOrderPopup(false)}>закрити</button>
            </div>
            {ordersQ.isLoading && <div>Завантаження…</div>}
            {!ordersQ.isLoading && !(ordersQ.data?.items ?? []).length && (
              <div style={{ color: 'var(--text-secondary)' }}>
                Немає рахунків цього покупця.
              </div>
            )}
            {(ordersQ.data?.items ?? []).length > 0 && (
              <TchTable>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Дата</th>
                    <th>Покупець</th>
                    <th style={{ textAlign: 'right' }}>Сума</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(ordersQ.data?.items ?? []).map((o) => (
                    <tr key={o.id} style={{ cursor: 'pointer' }}
                      onDoubleClick={() => {
                        setF((s) => ({ ...s, base_order_id: o.id,
                          contractor_id: s.contractor_id || o.contractor_id }));
                        setOrderPopup(false);
                        void fillRowsFromOrder(o.id, f.advance_amount);
                      }}>
                      <td>{o.document_number}</td>
                      <td>
                        {new Date(o.document_date).toLocaleDateString('uk-UA')}
                      </td>
                      <td>
                        {contractorName.get(o.contractor_id) ?? ''}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmt(Number(o.total_amount))}</td>
                      <td>
                        <button onClick={() => {
                          setF((s) => ({ ...s, base_order_id: o.id,
                            contractor_id: s.contractor_id || o.contractor_id }));
                          setOrderPopup(false);
                          void fillRowsFromOrder(o.id, f.advance_amount);
                        }}>обрати</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TchTable>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
